<script setup lang="ts">
import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core'
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common'

import { computed } from 'vue'
import {$t} from "../ts/translations";

const props = defineProps({
  password: {
    type: String,
    required: true
  }
})

// EAUTH2-316: better password strength using dictionaries and adjacency graphs
zxcvbnOptions.setOptions({
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
  },
})

/**
 * A password strength from 0 to 4.
 * See https://elodigital.atlassian.net/browse/EAUTH2-175
 *
 * Default zxcvbn scores:
 *   0 # very weak - guesses < 10^3
 *   1 # weak - guesses < 10^6
 *   2 # fair - guesses < 10^8
 *   3 # strong - guesses < 10^10
 *   4 # very strong - guesses >= 10^10
 *
 * Our mapping:
 *   0 # very weak - guesses < 10^5
 *   1 # weak - guesses < 10^8
 *   2 # fair - guesses < 10^10
 *   3 # strong - guesses < 10^12
 *   4 # very strong - guesses >= 10^12
 */
const strength = computed(() => {
  const result = zxcvbn(props.password)
  if (result.guessesLog10 < 5) {
    return 0
  } else if (result.guessesLog10 < 8) {
    return 1
  } else if (result.guessesLog10 < 10) {
    return 2
  } else if (result.guessesLog10 < 12) {
    return 3
  } else {
    return 4
  }
})
</script>

<template>
  <div class="password-strength">
    <div v-if="password.length > 0" :class="'strength-' + strength"></div>
  </div>
  <div class="has-text-right">
    <template v-if="password.length > 0">
      {{ $t('password_strength_' + strength) }}
    </template>
    <template v-else>
      &nbsp;
    </template>
  </div>
</template>

<style scoped>
.password-strength {
  width: 100%;
  height: 4px;
  border-right: 2px;
  background-color: lightgrey;
}

.password-strength > div {
  height: 100%;
  transition: width 0.5s ease-in-out,
              background-color 0.5s ease-in-out;
}

.strength-0 {
  background-color: #B23149;
  width: 20%;
}
.strength-1 {
  background-color: #FF9F19;
  width: 40%;
}
.strength-2 {
  background-color: #FFD147;
  width: 60%;
}
.strength-3 {
  background-color: #63A343;
  width: 80%;
}
.strength-4 {
  background-color: #187855;
  width: 100%;
}
</style>
